"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Icon } from "@repo/ui/icon";
import { cn } from "@repo/ui/utils";
import { CategoryNavLinks } from "./category-nav-links.component";
import type { CategoryNavItem, CategoryOption } from "./directory-listing.types";
interface DirectoryFilterBarProps {
  categoryOptions: CategoryOption[];
  selectedCategories: Set<string>;
  searchQuery: string;
  searchLabel: string;
  onCategoryToggle: (categoryId: string) => void;
  onClearFilters: () => void;
  onSearchChange: (query: string) => void;
  /**
   * Editor-curated category links (articles). When provided, the category row
   * renders navigation links to the archives instead of Algolia toggle pills.
   */
  categoryMenu?: CategoryNavItem[];
  categoryMenuAllHref?: string;
}
function SelectorTab(t0) {
  const $ = _c(11);
  let children;
  let props;
  let selected;
  if ($[0] !== t0) {
    ({
      selected,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = selected;
  } else {
    children = $[1];
    props = $[2];
    selected = $[3];
  }
  const t1 = selected ? "bg-button-primary-background text-button-primary-text" : "text-text-primary hover:bg-surface-base-secondary";
  let t2;
  if ($[4] !== t1) {
    t2 = cn("shrink-0 cursor-pointer whitespace-nowrap rounded-full transition-colors", "px-3 py-2 typography-component-xs-medium", "desktop:px-4 desktop:py-3 desktop:typography-component-sm-medium", t1);
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== children || $[7] !== props || $[8] !== selected || $[9] !== t2) {
    t3 = <button type="button" className={t2} aria-pressed={selected} {...props}>{children}</button>;
    $[6] = children;
    $[7] = props;
    $[8] = selected;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
export function DirectoryFilterBar(t0) {
  const $ = _c(55);
  const {
    categoryOptions,
    selectedCategories,
    searchQuery,
    searchLabel,
    onCategoryToggle,
    onClearFilters,
    onSearchChange,
    categoryMenu,
    categoryMenuAllHref
  } = t0;
  const t = useTranslations("directory");
  const menuMode = categoryMenu != null;
  const [searchOpen, setSearchOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState("");
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const cancelDebounce = t1;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => cancelDebounce;
    t3 = [cancelDebounce];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== searchQuery) {
    t4 = () => {
      setLocalSearch(searchQuery);
    };
    t5 = [searchQuery];
    $[3] = searchQuery;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== onSearchChange) {
    t6 = value => {
      setLocalSearch(value);
      cancelDebounce();
      debounceRef.current = setTimeout(() => {
        onSearchChange(value);
      }, 300);
    };
    $[6] = onSearchChange;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleSearchInput = t6;
  let t7;
  if ($[8] !== onSearchChange) {
    t7 = () => {
      setSearchOpen(prev => {
        const next = !prev;
        if (next) {
          requestAnimationFrame(() => searchInputRef.current?.focus());
        } else {
          cancelDebounce();
          setLocalSearch("");
          onSearchChange("");
        }
        return next;
      });
    };
    $[8] = onSearchChange;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const toggleSearch = t7;
  if (menuMode ? categoryMenu.length === 0 : categoryOptions.length === 0) {
    return null;
  }
  let t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = cn("col-span-full flex items-center gap-2 border-b border-border-default pb-3 desktop:hidden", "focus-within:border-border-active");
    t9 = <Icon name="search" className="size-5 shrink-0 text-text-primary" />;
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  let t10;
  if ($[12] !== handleSearchInput) {
    t10 = e => handleSearchInput(e.target.value);
    $[12] = handleSearchInput;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t) {
    t11 = t("search");
    $[14] = t;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = `${t("search")} ${searchLabel}`;
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = cn("w-full bg-transparent typography-body-sm-regular text-text-primary", "placeholder:text-text-secondary focus:outline-none");
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== localSearch || $[18] !== t10 || $[19] !== t11 || $[20] !== t12) {
    t14 = <label className={t8}>{t9}<input type="search" value={localSearch} onChange={t10} placeholder={t11} aria-label={t12} className={t13} /></label>;
    $[17] = localSearch;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = cn("flex min-w-0 flex-1 items-center gap-2 overflow-x-auto scrollbar-none", "desktop:flex-wrap desktop:overflow-x-visible");
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== categoryMenu || $[24] !== categoryMenuAllHref || $[25] !== categoryOptions || $[26] !== menuMode || $[27] !== onCategoryToggle || $[28] !== onClearFilters || $[29] !== selectedCategories || $[30] !== t) {
    t16 = <div className={t15}>{menuMode ? <CategoryNavLinks items={categoryMenu} allHref={categoryMenuAllHref} /> : <><SelectorTab selected={selectedCategories.size === 0} onClick={onClearFilters}>{t("all")}</SelectorTab>{categoryOptions.map(category => <SelectorTab key={category._id} selected={selectedCategories.has(category._id)} onClick={() => onCategoryToggle(category._id)}>{category.name}</SelectorTab>)}</>}</div>;
    $[23] = categoryMenu;
    $[24] = categoryMenuAllHref;
    $[25] = categoryOptions;
    $[26] = menuMode;
    $[27] = onCategoryToggle;
    $[28] = onClearFilters;
    $[29] = selectedCategories;
    $[30] = t;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  let t18;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = cn("hidden shrink-0 cursor-pointer items-center gap-2 typography-body-sm-medium text-text-primary", "hover:text-text-secondary transition-colors desktop:flex");
    t18 = <Icon name="search" className="size-5" />;
    $[32] = t17;
    $[33] = t18;
  } else {
    t17 = $[32];
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t) {
    t19 = t("search");
    $[34] = t;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t19) {
    t20 = <span>{t19}</span>;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== searchOpen || $[39] !== t20 || $[40] !== toggleSearch) {
    t21 = <button type="button" onClick={toggleSearch} aria-expanded={searchOpen} aria-controls="directory-search-panel" className={t17}>{t18}{t20}</button>;
    $[38] = searchOpen;
    $[39] = t20;
    $[40] = toggleSearch;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t16 || $[43] !== t21) {
    t22 = <div className="col-span-full flex items-center gap-2">{t16}{t21}</div>;
    $[42] = t16;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== handleSearchInput || $[46] !== localSearch || $[47] !== searchLabel || $[48] !== searchOpen || $[49] !== t) {
    t23 = searchOpen && <div id="directory-search-panel" className="col-span-full hidden desktop:block"><label htmlFor="directory-search" className="sr-only">{t("search")} {searchLabel}</label><input id="directory-search" ref={searchInputRef} type="text" value={localSearch} onChange={e_0 => handleSearchInput(e_0.target.value)} placeholder={t("searchPlaceholder", {
        label: searchLabel
      })} className={cn("w-full rounded-sm border border-border-default px-4 py-3", "typography-body-sm-regular text-text-primary placeholder:text-text-secondary", "focus:border-border-active focus:outline-none")} /></div>;
    $[45] = handleSearchInput;
    $[46] = localSearch;
    $[47] = searchLabel;
    $[48] = searchOpen;
    $[49] = t;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t14 || $[52] !== t22 || $[53] !== t23) {
    t24 = <>{t14}{t22}{t23}</>;
    $[51] = t14;
    $[52] = t22;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  return t24;
}